import cx from "classnames";
import { useState } from "react";
import type { Swiper as SwiperClass } from "swiper";
import { A11y, Thumbs } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { useTranslation } from "@/core/i18n";
import { IconButton } from "atoms/icon-button/IconButton";
import ArrowLeft from "atoms/icon/icons/arrow_left.svg";
import ArrowRight from "atoms/icon/icons/arrow_right.svg";
import { TextButton } from "atoms/text-button/TextButton";

import type { ImageCarouselProps } from "./ImageCarousel";

/**
 * The detail-page photo frame: landscape across a phone; on desktop the full height of the
 * viewport under the sticky header (65px, 73px from desklg); on the largest screens a rounded
 * portrait card kept short enough to sit whole in view.
 */
export const pdpFrameClassName =
  "aspect-[6/5] w-full desk:aspect-auto desk:h-[calc(100dvh-65px)] desklg:h-[calc(100dvh-73px)] desk3xl:aspect-[4/5] desk3xl:h-auto desk3xl:max-h-[calc(100dvh-73px-2.5rem)] desk3xl:rounded-2xl";

export const PdpImageCarousel = ({ slides, productName, frameClassName }: ImageCarouselProps) => {
  const { t } = useTranslation();
  const [gallery, setGallery] = useState<SwiperClass | null>(null);
  const [thumbnails, setThumbnails] = useState<SwiperClass | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  if (slides.length === 0) return null;

  return (
    <div className="relative w-full overflow-hidden bg-background-two desk3xl:rounded-2xl">
      <Swiper
        className={cx(pdpFrameClassName, frameClassName)}
        onSwiper={setGallery}
        onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
        thumbs={{ swiper: thumbnails }}
        modules={[A11y, Thumbs]}
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={`${slide.src}-${index}`}>
            <img
              src={slide.src}
              alt={slide.alt || productName || t("imageCarousel.productImage")}
              className="h-full w-full object-cover"
            />
          </SwiperSlide>
        ))}
      </Swiper>
      {slides.length > 1 && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex items-end justify-between gap-4 bg-gradient-to-t from-black/30 to-transparent px-5 pb-5 pt-16 desk:p-8 desk:pt-20">
          <Swiper
            onSwiper={setThumbnails}
            slidesPerView="auto"
            spaceBetween={8}
            watchSlidesProgress
            modules={[A11y, Thumbs]}
            className="pointer-events-auto !m-0 min-w-0 flex-1"
          >
            {slides.map((slide, index) => (
              <SwiperSlide key={`thumb-${slide.src}-${index}`} className="!w-12 desk:!w-16">
                <TextButton
                  className={cx(
                    "w-full rounded border-2 !p-0.5 transition-opacity",
                    activeIndex === index
                      ? "border-white"
                      : "border-transparent opacity-60 hover:opacity-100",
                  )}
                  onClick={() => {
                    if (gallery) gallery.slideTo(index);
                  }}
                >
                  <img
                    src={slide.src}
                    alt={slide.alt || productName || t("imageCarousel.productImage")}
                    className="aspect-square w-full rounded-sm object-cover"
                  />
                </TextButton>
              </SwiperSlide>
            ))}
          </Swiper>
          <div className="pointer-events-auto flex shrink-0 items-center gap-1 rounded-full bg-white/95 p-1 text-primary">
            <IconButton
              svg={ArrowLeft}
              style="iconOnly"
              size="small"
              aria-label={t("beforeAfterGallery.previousPhoto")}
              disabled={activeIndex === 0}
              onClick={() => {
                if (gallery) gallery.slidePrev();
              }}
            />
            <span className="min-w-8 text-center text-body3 tabular-nums" aria-live="polite">
              {activeIndex + 1} / {slides.length}
            </span>
            <IconButton
              svg={ArrowRight}
              style="iconOnly"
              size="small"
              aria-label={t("beforeAfterGallery.nextPhoto")}
              disabled={activeIndex === slides.length - 1}
              onClick={() => {
                if (gallery) gallery.slideNext();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
